<template>
  <div v-if="showComponent">
    <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/process:handling.title') }}</h4>
    <BaseButton
        :btn-text="$t('book/process:handling.btns.set-all')"
        color="warning"
        lower-case
        @clickedButton="setOpenStepsToSuccess"
    />

    <v-expansion-panels tile flat v-model="openedPanel" class="mt-3">
      <v-expansion-panel v-for="(step, i) in processStepsAndResults" :key="step.id" class="my-1">
        <!-- Panel Header -->
        <v-expansion-panel-header :class="'pa-3 border-left--' + getPanelBorderColor(step)">
          <v-row justify="space-between" align="center">
            <!-- State Icon and step name -->
            <v-col class="py-0">
              <v-row no-gutters>
                <v-icon large :color="getIconColor(step)">{{ getIcon(step) }}</v-icon>

                <!-- Process Step name -->
                <h6 class="mb-0 pt-2 ml-2 greyDark--text">{{ step.name }}</h6>
              </v-row>
            </v-col>

            <!-- Success/Failure Buttons -->
            <v-col class="py-sm-0 text-sm-right mr-sm-5">
              <!-- Success-Button -->
              <BaseButton
                  v-show="!step.success || !step.inProgress"
                  outlined
                  color="scaleGreen"
                  lower-case
                  :btn-text="$t('book/process:handling.btns.isSuccessfull')"
                  small
                  @click.native.stop="putSuccessfullStep(step, i)"
              />

              <!-- Failed-Button -->
              <BaseButton
                  v-show="step.success || !step.inProgress"
                  outlined
                  color="scaleRed"
                  lower-case
                  :btn-text="$t('book/process:handling.btns.isFailed')"
                  small
                  @click.native.stop="toggleFailedProcessHandling(step, i)"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-header>

        <!-- Panel Body -->
        <v-expansion-panel-content eager class="mt-2">
          <p>{{ step.description }}</p>

          <div v-show="(!step.success && step.inProgress) || (!step.success && step.finished)">
            <v-form :ref="'formPart1_' + i">
              <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('common:answer') }}</h6>
              <v-row>
                <v-col cols="12" lg="7" class="m-0">
                  <v-select
                      outlined
                      v-model="step.answer"
                      :label="$t('book/process:handling.labels.result')"
                      :menu-props="{ bottom: true, offsetY: true }"
                      :items="step.answers"
                  />
                </v-col>
                <v-col cols="12" lg="7" v-if="step.userAddsAnswer">
                  <!-- Input additional Information-->
                  <v-text-field
                      outlined
                      v-model="step.additionalAnswer"
                      :label="$t('book/process:handling.labels.alt-result')"
                  />
                </v-col>
                <!-- Checkbox additional Information-->
                <v-col cols="12" lg="5" v-if="step.userAddsAnswer">
                  <v-checkbox
                      v-model="addAddInfoCheckbox"
                      v-bind:label="$t('book/process:handling.labels.alt-checkbox')"
                      color="secondary"
                  />
                </v-col>
              </v-row>
            </v-form>

            <!-- Documentation upload and display section -->
            <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('common:documentation_plural') }}</h6>
            <div class="d-flex flex-column flex-sm-row flex-sm-wrap">
              <ProcessDocuUploadForm
                  :ref="'docuUploadForm_' + i"
                  @documentationCreated="pushDocumentToStep($event, step, i)"
              />

              <v-card
                  @click.native.stop="$refs['cameraUploadForm_' + i][0].showDialog = true"
                  class="add-picture mx-5"
                  elevation="0">
                <v-card-text class="my-auto">
                  <v-row class="d-flex flex-column" dense align="center" justify="center">
                    <v-icon class="mt-5" size="60">
                      add_a_photo
                    </v-icon>
                    <CameraUploadForm
                        :ref="'cameraUploadForm_' + i"
                        color="primary"
                        hideButton
                        @postFile="postCamFile($event, step, i)"
                    />
                    <p>
                      {{ $t('book/process:handling.btns.take-photo') }}
                    </p>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Display documentation section -->
              <div v-for="docu in step.documentation" :key="docu.localId" class="document-preview-wrapper">
                <div class="mt-5 mt-sm-0 mr-sm-5">
                  <v-btn icon color="scaleRed" @click="deleteDocumentation(docu, step, i, 'dialogDeleteDocumentation_' + i)">
                    <v-icon>
                      close
                    </v-icon>
                  </v-btn>
                  <pdf
                      ref="pdf"
                      v-if="docu.fileType === 'application/pdf'"
                      :src="docu.src"
                      class="pdf docu-preview"
                      v-on:click.native="viewDocumentation(docu, step)"
                  >
                  </pdf>

                  <img
                      v-else
                      :src="docu.src"
                      class="docu-preview"
                      @click="viewDocumentation(docu, step)"
                  />
                </div>
                <TwoButtonDialog
                    :ref="'dialogDeleteDocumentation_' + i"
                    hideButton
                    :btn-text="$t('buttons:delete')" color="error"
                    :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/process:handling.dialog.documentation-dialog-1')})"
                    :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/process:handling.dialog.documentation-dialog-2')})"
                    :button1-text="$t('buttons:cancel')"
                    :button2-text="$t('buttons:delete')"
                    @clickedButton1="$refs['dialogDeleteDocumentation_' + i][0].showDialog = false"
                    @clickedButton2="deleteDocumentationConfirmed(i, 'dialogDeleteDocumentation_' + i)"
                />
              </div>
            </div>

            <!-- Form fields for failed process step -->
            <v-form :ref="'formPart2_' + i">
              <v-row>
                <v-col cols="12" md="6">
                  <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('common:comment') }}</h6>
                  <v-textarea
                      outlined
                      v-model="step.failureText"
                      auto-grow
                      :placeholder="$t('book/process:handling.labels.error-msg')"
                  />
                </v-col>
                <v-col v-if="processJob == null || processJob.disableDeadline == false" cols="12" md="6">
                  <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('book/process:handling.deadline') }}</h6>
                  <DateField
                      ref="defectDeadline"
                      @update="(v) => (step.defectDeadline = v)"
                      :isClearable="true"
                      :isRequired="false"
                      :label="$t('book/process:handling.labels.deadline')"
                      only-future
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <TwoButtonDialog
                      :ref="'dialogFail_' + i"
                      :btn-text="$t('buttons:save')"
                      color="scaleRed"
                      showAlertProp
                      alertTypeProp="warning"
                      :alertTextProp="$t('dialog:alert-warning')"
                      :subtitle-text="$t('book/process:handling.failed-dialog.subtitle')"
                      :add-info-text="step.userMessage"
                      :dialog-text="$t('book/process:handling.failed-dialog.text')"
                      :button1-text="$t('book/process:handling.failed-dialog.confirm-btn')"
                      :button2-text="$t('buttons:cancel')"
                      @clickedButton1="putFailedStep(step, i, 'formPart1_' + i, 'formPart2_' + i, 'dialogFail_' + i)"
                      @clickedButton2="$refs['dialogFail_' + i][0].showDialog = false"
                  />

                  <BaseButton
                      color="grey"
                      :btnText="$t('buttons:cancel')"
                      @clickedButton="cancelFailedProcessHandling(step, i)"
                  />
                </v-col>
                <v-col class="text-right">
                  <BaseButton
                      color="grey"
                      btnText="Weiter"
                      @clickedButton="putFailedStep(step, i, 'formPart1_' + i, 'formPart2_' + i, 'dialogFail_' + i)"
                  />
                </v-col>
              </v-row>
            </v-form>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <div class="mt-5" >

      <TwoButtonDialog
          v-if="checkStates"
          ref="finishBtn"
          :btn-text="$t('book/process:handling.dialog.text')"
          color="primary"
          :dialog-title="$t('book/process:handling.dialog.title')"
          :dialog-text="$t('book/process:handling.dialog.dialog')"
          :button1-text="$t('book/process:handling.dialog.btn1-text')"
          :button2-text="$t('buttons:cancel')"
          @clickedButton1="$emit('finishProcess')"
          @clickedButton2="$refs.finishBtn.showDialog = false"
      />
      <BaseButton
          color="scaleGreen"
          :btn-text="$t('buttons:generate-report')"
          @clickedButton="$emit('generateReport')"
      />
      <BaseButton
          color="grey"
          :btn-text="$t('buttons:cancel')"
          @clickedButton="$emit('cancelProcess')"
      />
    </div>

    <!-- Additional Components -->
    <DocumentViewer hide-overlay ref="documentViewer" offline/>
  </div>
</template>
<script>
import BaseButton from "@/modules/core/components/Base/BaseButton";
import ProcessDocuUploadForm from "@/modules/book/views/process/ProcessDocuUploadForm";
import CameraUploadForm from "@/modules/core/components/CameraUploadForm";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import DateField from "@/modules/core/components/DateField";
import Process from "@/modules/book/data/process";
import ProcessJob from "@/modules/book/data/processJob";
import ProcessResult from "@/modules/book/data/processResult";
import ProcessService from "@/modules/book/services/process/impl/process-service";
import DataUtils from "@/modules/core/services/data-utils";
import ProcessStep from "@/modules/book/data/processStep";
import indexedDBService from "@/modules/core/services/indexedDB-service";
import ProcessResultService from "@/modules/book/services/process/impl/process-result-service";
import Utils from "@/modules/core/services/utils";
import ProcessStepResult from "@/modules/book/data/processStepResult";
import Vue from "vue";
import Documentation from "@/modules/book/data/documentation";
import {ruleRequired} from "@/modules/core/services/rules";

export default {
  name: "ProcessHandling",
  components: {
    DocumentViewer: () => import('@/modules/core/components/DocumentViewer/DocumentViewer'),
    DateField,
    TwoButtonDialog,
    CameraUploadForm,
    BaseButton,
    ProcessDocuUploadForm,
    pdf: () => import('vue-pdf')
  },
  props: {
    quickstart: Boolean
  },
  data() {
    return {
      process: new Process(),
      processJob: new ProcessJob(),
      processResult: new ProcessResult(),
      processStepsAndResults: [],

      openedPanel: null,
      showComponent: false,
      addAddInfoCheckbox: false,
      deleteDocu: {
        docuId: null,
        step: null,
        stepIndex: null
      }
    }
  },
  computed: {
    checkStates() {
      let finishedCounter = 0;
      for(let i = 0; i < this.processStepsAndResults.length; i++) {
        if(this.processStepsAndResults[i].finished) {
          finishedCounter++;
        }
      }

      if(finishedCounter === this.processStepsAndResults.length) {
        return true;
      } else {
        return false;
      }
    },

    currentLang() {
      return this.$store.state.translation.language;
    },
  },
  methods: {
    ruleRequired,
    /**
     * Checks the step state and returns a string, with which the border color style
     * of the expansion panel is set.
     *
     * @param step step to check state
     * @returns {string} string for css settings
     */
    getPanelBorderColor(step) {
      if(step.finished) {
        if(step.success) {
          return 'successfull'
        } else {
          return 'failed'
        }
      } else if(step.inProgress) {
        if(step.success) {
          return 'successfull'
        } else {
          return 'failed'
        }
      } else {
        return 'open'
      }
    },

    /**
     * Checks the step state and returns a color for the state icon.
     *
     * @param step step to check state
     * @returns {string} color
     */
    getIconColor(step) {
      if (!step.finished && !step.inProgress) {
        return "greyTint"
      } else if ((step.finished && step.success) || (step.inProgress && step.success)) {
        return "scaleGreen"
      } else if ((step.finished && !step.success) || (step.inProgress && !step.success)) {
        return "scaleRed"
      } else {
        return ""
      }
    },

    /**
     * Checks the step state and returns a string which defines the type of icon.
     *
     * @param step step to check state
     * @returns {string} icon type
     */
    getIcon(step) {
      if (!step.finished && !step.inProgress) {
        return "radio_button_unchecked"
      } else if ((step.finished && step.success) || (step.inProgress && step.success)) {
        return "check_circle_outline"
      } else if ((step.finished && !step.success) || (step.inProgress && !step.success)) {
        return "check_circle_outline"
      }
    },

    /**
     * Checks all process steps for their state.
     * If all process steps are successful or if one step is failed,
     * an event is emitted.
     */
    checkTaskStatus() {
      let successCounter = 0;
      for(let i = 0; i < this.processStepsAndResults.length; i++) {
        // check if step has been finished
        if(this.processStepsAndResults[i].finished) {
          if(this.processStepsAndResults[i].success) {
            successCounter++;
            if(successCounter == this.processStepsAndResults.length) {
              this.$emit('allSuccessfull')
              break;
            }
          } else {
            this.$emit('oneStepFailed')
            break;
          }
        }
      }
    },

    /**
     * Checks if the process steps and results have already been saved once in the indexedDB.
     * If so, they will be read, if not they will be saved to the indexedDB
     *
     * @returns {Promise<void>}
     */
    async checkStepsAndResultsIndexedDB() {
      let promises = [];
      for (let i = 0; i < this.processStepsAndResults.length; i++) {
        promises.push(new Promise(resolve => {
          indexedDBService.read(this.processStepsAndResults[i].id, 'processStepsAndResults').then((res) => {
            if (!res) {
              indexedDBService.save(this.processStepsAndResults[i], 'processStepsAndResults').then(() => {
                resolve();
              })
            } else {
              this.processStepsAndResults[i] = res;
              resolve();
            }
          })
        }))
      }

      await Promise.all(promises);
    },

    /**
     * Inits the process steps and process steps results
     */
    fillProcessStepsAndResults() {
      // insert stepResult data into merged array
      for(let i = 0; i < this.processResult.stepResults.length; i++) {
        for(let j = this.processStepsAndResults.length -1; j >= 0; j--) {
          if(this.processResult.stepResults[i].step == this.processStepsAndResults[j].id) {
            this.processStepsAndResults[j].answer = this.processResult.stepResults[i].answer;
            this.processStepsAndResults[j].additionalAnswer = this.processResult.stepResults[i].additionalAnswer;
            this.processStepsAndResults[j].failureText = this.processResult.stepResults[i].failureText;

            this.processStepsAndResults[j].documentation = this.processResult.stepResults[i].documentation;

            this.processStepsAndResults[j].success = this.processResult.stepResults[i].success;
            this.processStepsAndResults[j].finished = this.processResult.stepResults[i].finished;
            this.processStepsAndResults[j].defectDeadline = this.processResult.stepResults[i].defectDeadline;

            this.processStepsAndResults[j].inProgress = true;
            this.processStepsAndResults[j].isBeingEdited = true;
            indexedDBService.save(this.processStepsAndResults[j], 'processStepsAndResults')

            break;
          }
        }
      }

      // if steps has not been finished, set the default values
      for(let k = 0; k < this.processStepsAndResults.length; k++) {
        if(!this.processStepsAndResults[k].finished) {
          this.processStepsAndResults[k].documentation = [];
          this.processStepsAndResults[k].inProgress = false;
          this.processStepsAndResults[k].isBeingEdited = false;

          indexedDBService.save(this.processStepsAndResults[k], 'processStepsAndResults')
        }
      }

      this.checkTaskStatus();

      // show component so the colors are being displayed right
      this.showComponent = true;

      //remove the excluded process steps
      if(!this.quickstart && this.processJob.excludedSteps && this.processJob.excludedSteps.length > 0) {
        this.process.processSteps = this.process.processSteps.filter(
            (step) => !this.processJob.excludedSteps.includes(step)
        );
        for (let i = 0; i < this.processStepsAndResults.length; i++) {
          for (let j = 0; j < this.processJob.excludedSteps.length; j++) {
            if(this.processStepsAndResults[i].id == this.processJob.excludedSteps[j]){
              this.processStepsAndResults.splice(i,1);
              indexedDBService.delete(this.processStepsAndResults[i].id, 'processStepsAndResults');
            }
          }
        }
      }
    },

    // set step to state successfull and update processResult
    putSuccessfullStep(step, index) {
      for(let i = 0; i < this.processStepsAndResults.length; i++) {
        if(this.processStepsAndResults[i].id == step.id) {
          let stepResult = new ProcessStepResult();
          stepResult.success = true;
          stepResult.step = this.processStepsAndResults[i].id;
          stepResult.name = this.processStepsAndResults[i].name;
          stepResult.description = this.processStepsAndResults[i].description;
          stepResult.answer = "";
          stepResult.additionalAnswer = "";
          stepResult.failureText = "";
          stepResult.documentation = [];
          stepResult.finished = true;

          // if stepResult is being edited, clear failure data and update result
          if(this.processStepsAndResults[i].finished) {
            for(let j = 0; j < this.processResult.stepResults.length; j++) {
              if(stepResult.step == this.processResult.stepResults[j].step) {
                this.processResult.stepResults[j] = stepResult;
              }
            }
          } else {
            this.processResult.stepResults.push(stepResult)
          }

          step.success = true;
          step.finished = true;
          step.inProgress = true;
          step.isBeingEdited = true;
          Vue.set(this.processStepsAndResults, i, step);

          indexedDBService.save(this.processStepsAndResults[i], 'processStepsAndResults').then(() => {
            indexedDBService.save(this.processResult, 'processResult').then(() => {
              Utils.showSuccessSnackbar(this.$t('snackbar:finish-success', {name: this.$t('common:process-step')}));
              this.checkTaskStatus()
              if(index + 1 <= this.processStepsAndResults.length) {
                this.openedPanel = index + 1;
              } else {
                this.openedPanel = null;
              }
            }).catch(() => {
              Utils.showFailureSnackbar(this.$t('snackbar:finish-failure', {name: this.$t('common:process-step')}));
            })
          })
        }
      }
    },

    putFailedStep(step, index, form1Ref, form2Ref, dialogRef) {
      if (!this.$refs[form1Ref][0].validate() || !this.$refs[form2Ref][0].validate()) {
        this.$refs[dialogRef][0].showDialog = false;
        return;
      }

      if(step.documentationRequired && step.documentation.length == 0) {
        Utils.showWarningSnackbar(this.$t('book/process:handling.doc-required'));
        this.$refs[dialogRef][0].showDialog = false;
        return
      }

      let stepResult = new ProcessStepResult();
      stepResult.success = false;
      stepResult.finished = true;

      stepResult.step = step.id;
      stepResult.name = step.name;
      stepResult.description = step.description;

      if (!this.addAddInfoCheckbox) {
        stepResult.additionalAnswer = "";
      } else {
        stepResult.additionalAnswer = step.additionalAnswer;
      }

      stepResult.answer = step.answer;
      stepResult.failureText = step.failureText;
      stepResult.documentation = [];
      stepResult.defectDeadline = step.defectDeadline;

      if(step.documentation) {
        for(let i = 0; i < step.documentation.length; i++) {
          stepResult.documentation.push(step.documentation[i])
        }
      }

      if(step.isBeingEdited) {
        for(let i = 0; i < this.processResult.stepResults.length; i++) {
          if(this.processResult.stepResults[i].step == stepResult.step) {
            this.processResult.stepResults[i] = stepResult;
          }
        }
      } else {
        this.processResult.stepResults.push(stepResult);
      }

      this.$refs[dialogRef][0].setButtonSettings(true, true, false, true);

      step.success = false;
      step.finished = true;
      step.inProgress = true;
      step.isBeingEdited = true;
      Vue.set(this.processStepsAndResults, index, step);

      indexedDBService.save(this.processStepsAndResults[index], 'processStepsAndResults').then(() => {
        indexedDBService.save(this.processResult, 'processResult').then(() => {
          Utils.showSuccessSnackbar(this.$t('snackbar:finish-success', {name: this.$t('common:process-step')}));
          this.$refs[dialogRef][0].setButtonSettings(false, false, false, false);
          this.$refs[dialogRef][0].showDialog = false;

          this.checkTaskStatus()

          if(index + 1 <= this.processStepsAndResults.length) {
            this.openedPanel = index + 1;
          } else {
            this.openedPanel = null;
          }
        }).catch(() => {
          Utils.showFailureSnackbar(this.$t('snackbar:finish-failure', {name: this.$t('common:process-step')}));
          this.$refs[dialogRef][0].setButtonSettings(false, false, false, false);
          this.$refs[dialogRef][0].showDialog = false;
        })
      })
    },

    // open expansion panel and show input fields
    // to handle failed process step
    toggleFailedProcessHandling(step, index) {
      if(this.openedPanel == index) {
        this.openedPanel = null;
      }
      this.openedPanel = index;
      step.success = false;
      step.inProgress = true;
      Vue.set(this.processStepsAndResults, index, step)
    },

    // sets step to unprocessing and closes panel
    cancelFailedProcessHandling(step, index) {
      step.success = false;
      step.inProgress = false;
      Vue.set(this.processStepsAndResults, index, step);

      this.openedPanel = null;
    },

    /**
     * Checks if the processResult has already been saved to the indexedDB.
     * If not, save it to the DB.
     */
    checkProcessResultIndexedDB() {
      indexedDBService.read(this.processResult.id, 'processResult').then((res) => {
        if (!res) {
          indexedDBService.save(this.processResult, 'processResult').then(() => {
            this.fillProcessStepsAndResults();
          })
        } else {
          this.processResult = res;
          this.fillProcessStepsAndResults();
        }
      })
    },

    /*
     *  Documentation methods
     */
    pushDocumentToStep(document, step, panelIndex) {
      if(!step.documentation) {
        step.documentation = [];
      }

      step.documentation.push(document)

      // update processStepsAndResults Array to see uploaded document immediately
      Vue.set(this.processStepsAndResults, panelIndex, step)
      indexedDBService.save(this.processStepsAndResults[panelIndex], 'processStepsAndResults')
    },

    postCamFile(args, step, index) {
      let reader = new FileReader();
      const startDate = args.documentation.startDate;
      const endDate = args.documentation.endDate;
      const name = args.documentation.name;
      const file = args.file;

      reader.addEventListener("load", () => {
        let document = new Documentation();
        document.startDate = startDate;
        document.endDate = endDate;
        document.name = name;
        document.file = file;
        document.toSave = true;
        document.src = reader.result;
        document.fileType = file.type;
        document.localId = Utils.generateUUIDv4();

        this.pushDocumentToStep(document, step, index);
      })

      if (file) {
        reader.readAsDataURL(file);
      }
    },

    viewDocumentation(data, step) {
      this.$refs.documentViewer.initViewer(step.documentation, data.fileName);
    },

    deleteDocumentation(docu, step, stepIndex, ref) {
      this.deleteDocu.docuId = docu.localId;
      this.deleteDocu.step = step;
      this.deleteDocu.stepIndex = stepIndex;
      this.$refs[ref][0].showDialog = true;
    },

    deleteDocumentationConfirmed(panelIndex, ref) {

      this.$refs[ref][0].setButtonSettings(false, false, false, false);
      this.$refs[ref][0].showDialog = false;
      Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:document')}));

      this.deleteDocu.step.documentation = this.deleteDocu.step.documentation.filter(entry => entry.localId != this.deleteDocu.docuId);

      Vue.set(this.processStepsAndResults, this.deleteDocu.stepIndex, this.deleteDocu.step);
      indexedDBService.save(this.processStepsAndResults[this.deleteDocu.stepIndex], 'processStepsAndResults')
      this.deleteDocu.docuId = null;
      this.deleteDocu.step = null;
      this.deleteDocu.stepIndex = null;
    },

    // fetch processStepData when process handling is loaded for the first time
    init(process, processJob, facility) {
      this.process = process;
      this.processJob = processJob;

      // get processSteps
      ProcessService.readProcessStepsFromAPI(this.process.id,
          (res) => {
            this.processStepsAndResults = DataUtils.parseDataToObjectArray(res.data, ProcessStep);

            this.checkStepsAndResultsIndexedDB().then(() => {
              if (!this.quickstart) {
                ProcessResultService.readFromAPI(this.processJob.result,
                    (result) => {
                      this.processResult = DataUtils.parseDataToSingleObject(result.data, ProcessResult);

                      this.checkProcessResultIndexedDB();
                      this.$emit('finishedFetchingData')
                    }
                )
              } else {
                // set the id of the processResult equal to the process id, so we have a constant identifier
                // for the indexedDB. Will be set to null before posting the quickstarted processResult
                this.processResult.id = process.id;
                this.processResult.facility = facility.id;
                this.processResult.facilityName = facility.name;
                this.processResult.process = process.id;
                this.processResult.processName = process.name;

                this.checkProcessResultIndexedDB();
                this.$emit('finishedFetchingData')
              }
            })
          },
          (error) => {
            this.$logger.error("Error at getting ProcessSteps of Process, because: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:process-step_plural')}))
          }
      )
    },

    // sets all unfinished steps to sucessfull
    setOpenStepsToSuccess() {
      for (let i = 0; i < this.processStepsAndResults.length; i++) {
        if (!this.processStepsAndResults[i].finished) {
          this.putSuccessfullStep(this.processStepsAndResults[i], i);
        }
      }
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()
      });
    },

    // when panel is being opened
    openedPanel() {
      if (this.openedPanel != null
          && this.processResult.stepResults[this.openedPanel]
          && !this.processResult.stepResults[this.openedPanel].success
          && this.processResult.stepResults[this.openedPanel].defectDeadline) {
        this.$refs.defectDeadline[this.openedPanel].parseDTO(this.processResult.stepResults[this.openedPanel].defectDeadline)
      }
    }
  }
}
</script>
<style src="@/modules/book/views/process/Process.scss" lang="scss"/>